import parsePhoneNumber, { type E164Number } from 'libphonenumber-js';
import { useEffect, type ChangeEvent } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Button, Card, Stack, TextField, Typography } from '@mui/material';
import { PhoneInput, Switch } from '@sage/ui-components';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';

import MainNav from 'src/components/MainNav';
import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import { PreferredCountryCodes, SupportedCountryCodes } from 'src/constants/SupportedCountryCodes';
import { TeamRoles } from 'src/constants/TeamRoles';
import { useAnalytics } from 'src/contexts/AnalyticsContext';
import { useSnackbar } from 'src/contexts/SnackbarContext';
import useResponsive from 'src/hooks/useResponsive';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { InnerMaxWidthContainer } from 'src/layouts/InnerMaxWidthContainer';
import { useInviteTeamMemberMutation, type TeamMemberRole } from 'src/store/sageHomeApi';
import {
    getPhoneNumberValidationRules,
    limitPhoneNumberLength,
} from 'src/utils/phoneNumberInputUtils';

export const Route = createFileRoute('/(app)/care/team/invite')({
    component: InviteComponent,
    validateSearch: (search: Record<string, unknown>) => {
        return {
            role: (search.role as string) || TeamRoles.CommunityCaregiver,
        };
    },
});

const PageName = 'Invite Team Member Page';

function InviteComponent() {
    const { isMobile } = useResponsive();
    const { role } = useSearch({ from: '/(app)/care/team/invite' });
    const navigate = useNavigate();

    const { trackPageView } = useAnalytics();
    const { showError, showSuccess } = useSnackbar();
    const { currentTeam } = useTeamMemberData();

    const [inviteTeamMember, { isLoading: isInviting }] = useInviteTeamMemberMutation();
    const { handleSubmit, control, formState } = useForm<{
        firstName: string;
        lastName: string;
        phoneNumber: string;
        email: string;
        isTeamAdmin: boolean;
    }>({
        mode: 'onChange',
        defaultValues: {
            firstName: '',
            lastName: '',
            phoneNumber: '',
            email: '',
            isTeamAdmin: false,
        },
    });

    const onSubmit = async (data: {
        firstName: string;
        lastName: string;
        phoneNumber: string;
        email: string;
        isTeamAdmin: boolean;
    }) => {
        if (!currentTeam?.teamRid) {
            console.error('No team selected');
            return;
        }

        const teamRole = data.isTeamAdmin ? TeamRoles.TeamAdmin : (role as TeamMemberRole);

        try {
            const invitedMember = await inviteTeamMember({
                body: {
                    teamRid: currentTeam.teamRid,
                    teamRole: teamRole,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    email: data.email,
                    phoneNumber: parsePhoneNumber(data.phoneNumber)?.number as E164Number,
                    isTeamAdmin: data.isTeamAdmin,
                },
            }).unwrap();

            showSuccess('Team member invited successfully');
            navigate({
                to: '/care/team/member/$teamMemberRid',
                params: { teamMemberRid: invitedMember.teamMemberRid },
                search: { fromInvite: true },
            });
        } catch (error) {
            console.error('Failed to invite team member:', error);
            showError('An error occurred, please try again.');
        }
    };

    const getRoleDisplayName = (role: string) => {
        switch (role) {
            case TeamRoles.LicensedCaregiver:
                return 'Licensed Caregiver';
            case TeamRoles.CommunityCaregiver:
                return 'Friends & Family';
            case TeamRoles.CareRecipient:
                return 'Care Recipient';
            default:
                return 'Team Member';
        }
    };

    const handlePhoneNumberChange = (value: string, onChange: (value: string) => void) => {
        if (value.startsWith('+1 1 ')) {
            onChange(limitPhoneNumberLength(value.replace('+1 1 ', '+1 ')));
        } else if (!value.startsWith('+1')) {
            onChange(limitPhoneNumberLength(`+1 ${value}`));
        } else {
            onChange(limitPhoneNumberLength(value));
        }
    };

    useEffect(() => {
        trackPageView(PageName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <InnerMaxWidthContainer>
            <PageHeader pageTitle={`Invite ${getRoleDisplayName(role)}`} />

            <Stack sx={{ gap: 4 }}>
                {role === TeamRoles.CommunityCaregiver && (
                    <Card variant="outlined" sx={{ p: 2.5, boxShadow: 'none' }}>
                        <Stack direction="row" sx={{ gap: 2 }}>
                            <Stack sx={{ gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Assign as admin
                                </Typography>
                                <Typography variant="body2">
                                    Give this family member access to manage visits, care notes, and
                                    the Care Circle. A valid phone and email are required.
                                </Typography>
                            </Stack>
                            <Controller
                                name="isTeamAdmin"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <Switch
                                        checked={value}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                            onChange(e.target.checked)
                                        }
                                    />
                                )}
                            />
                        </Stack>
                    </Card>
                )}

                <Controller
                    name="phoneNumber"
                    control={control}
                    rules={getPhoneNumberValidationRules({ isRequired: true })}
                    render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                        <PhoneInput
                            onlyCountries={SupportedCountryCodes}
                            preferredCountries={PreferredCountryCodes}
                            defaultCountry={PreferredCountryCodes[0]}
                            value={value ?? ''}
                            inputRef={ref}
                            onChange={(value) => handlePhoneNumberChange(value, onChange)}
                            error={!!error}
                            helperText={error?.message}
                        />
                    )}
                />

                <Controller
                    name="firstName"
                    control={control}
                    render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                        <TextField
                            inputRef={ref}
                            fullWidth
                            label="First Name"
                            variant="outlined"
                            value={value}
                            onChange={onChange}
                            error={!!error}
                            helperText={error?.message}
                        />
                    )}
                    rules={{ required: 'First name is required' }}
                />

                <Controller
                    name="lastName"
                    control={control}
                    render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                        <TextField
                            inputRef={ref}
                            fullWidth
                            label="Last Name"
                            variant="outlined"
                            value={value}
                            onChange={onChange}
                            error={!!error}
                            helperText={error?.message}
                        />
                    )}
                    rules={{ required: 'Last name is required' }}
                />

                <Controller
                    name="email"
                    control={control}
                    render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                        <TextField
                            inputRef={ref}
                            fullWidth
                            label="Email"
                            variant="outlined"
                            value={value}
                            onChange={onChange}
                            error={!!error}
                            helperText={error?.message}
                        />
                    )}
                    rules={{
                        required: 'Email is required',
                        pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Invalid email address',
                        },
                    }}
                />
            </Stack>

            <PageFooter includeMainNav>
                <Button
                    variant="contained"
                    fullWidth
                    color="primary"
                    onClick={handleSubmit(onSubmit)}
                    loading={isInviting}
                    disabled={!formState.isValid || isInviting}
                >
                    {isInviting ? 'Sending Invite...' : 'Send Invite'}
                </Button>
            </PageFooter>

            {isMobile && <MainNav />}
        </InnerMaxWidthContainer>
    );
}
